import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { PartnerShopListItem } from '@/types/partnerShop';

interface PartnerShopPrevMobileCardProps {
  shop: PartnerShopListItem;
}

function getShopDisplayName(shop: PartnerShopListItem): string {
  return shop.displayName || shop.shopName;
}

function getShopLocation(shop: PartnerShopListItem): string {
  return shop.areaName || shop.branchName || '';
}

function getShopPhotoCount(shop: PartnerShopListItem): number {
  return Number(shop.reviewPhotoCount || 0) + Number(shop.lookbookPhotoCount || 0);
}

function getShopImages(shop: PartnerShopListItem): string[] {
  const images = [
    ...(shop.heroImages || []),
  ].filter((image): image is string => Boolean(image));

  return Array.from(new Set(images));
}

export default function PartnerShopPrevMobileCard({ shop }: PartnerShopPrevMobileCardProps) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  // console.log(shop)
  const images = useMemo(() => getShopImages(shop), [shop]);
  const displayName = getShopDisplayName(shop);
  const location = getShopLocation(shop);
  const photoCount = getShopPhotoCount(shop);
  const activeImage = images[activeImageIndex] || '';
  const hasMultipleImages = images.length > 1;

  const changeImage = (event: MouseEvent<HTMLButtonElement>, direction: 'prev' | 'next') => {
    event.preventDefault();
    event.stopPropagation();

    setActiveImageIndex((current) => {
      if (direction === 'prev') return current === 0 ? images.length - 1 : current - 1;
      return current === images.length - 1 ? 0 : current + 1;
    });
  };

  const saveListScrollPosition = () => {
    sessionStorage.setItem('partner-shop-list:returnPath', `${window.location.pathname}${window.location.search}`);
    sessionStorage.setItem('partner-shop-list:scrollY', String(window.scrollY));
  };

  return (
    <Link
      to={`/detail/${shop.shopSlug}`}
      onClick={saveListScrollPosition}
      className="block overflow-hidden rounded-2xl border border-[rgba(112,115,124,0.16)] bg-white text-[#17171A] transition-shadow duration-120 hover:text-[#17171A] hover:shadow-[0_2px_8px_rgba(23,23,23,0.08),0_8px_24px_rgba(23,23,23,0.10)]"
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-[#F4F4F5]">
        {activeImage ? (
          <div
            role="img"
            aria-label={displayName}
            className="h-full w-full bg-cover bg-center bg-no-repeat transition-opacity duration-200"
            style={{ backgroundImage: `url("${activeImage}")` }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#F4F4F5]">
            <i className="ri-image-line text-3xl text-[#B0B8C1]"></i>
          </div>
        )}

        {shop.hasPromotion && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-[5px] rounded-lg bg-[#A84F38] px-[11px] py-1.5 text-[12px] font-bold tracking-[0.01em] text-white shadow-[0_2px_8px_rgba(168,79,56,0.35)]">
            <i className="ri-percent-line text-[13px]"></i>
            5% 캐시백
          </span>
        )}

        {location && (
          <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-[#17171A]">
            {location}
          </span>
        )}

        {hasMultipleImages && (
          <>
            <button
              type="button"
              onClick={(event) => changeImage(event, 'prev')}
              className="absolute left-2.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-white/90 text-[#17171A] shadow-[0_1px_4px_rgba(23,23,23,0.12)] transition-colors hover:bg-white"
              aria-label="이전 이미지"
            >
              <i className="ri-arrow-left-s-line text-[15px]"></i>
            </button>
            <button
              type="button"
              onClick={(event) => changeImage(event, 'next')}
              className="absolute right-2.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-white/90 text-[#17171A] shadow-[0_1px_4px_rgba(23,23,23,0.12)] transition-colors hover:bg-white"
              aria-label="다음 이미지"
            >
              <i className="ri-arrow-right-s-line text-[15px]"></i>
            </button>
          </>
        )}

        {images.length > 0 && (
          <span className="absolute bottom-2.5 right-3 rounded-full bg-black/50 px-[9px] py-[3px] text-[11px] font-medium text-white">
            {activeImageIndex + 1} / {images.length}
          </span>
        )}
      </div>

      <div className="px-5 pb-5 pt-[18px]">
        <h3 className="m-0 mb-1 text-[18px] font-bold leading-tight tracking-[-0.012em] text-[#17171A]">
          {displayName}
        </h3>

        <p className="m-0 text-[12px] text-[rgba(55,56,60,0.4)]">
          실제 후기 {Number(shop.reviewCount || 0).toLocaleString()}건 · 사진 {photoCount.toLocaleString()}장
        </p>
      </div>
    </Link>
  );
}
